// hotkeys.ts

import { derived, get } from "svelte/store";
import { authStore, searchPreferences } from "$lib/stores";
import { showKeyboardShortcuts } from "$lib/stores/keyboardShortcutsStore";
import { SUBSCRIPTION_TIER } from "$lib/types";
import type { AuthState } from "$lib/types";
import { searchFormOverlayStore } from "$lib/stores/searchFormOverlayStore";
import { page } from "$app/stores";
import { goto } from "$app/navigation";
import { routes } from "$lib/routes";
import { browser } from "$app/environment";
import { hotkeysStore } from "$lib/stores/hotkeysStore";
import { sidePanelStore } from "$lib/stores/sidePanelStore";

import { setMode, mode } from "mode-watcher";

export type HotkeyAction = {
  id: string;
  keys?: string;
  description: string;
  action: (event?: Event) => void;
  keyMatcher?: (event: KeyboardEvent) => boolean;
  isVisible?: () => boolean;
  isEnabled?: () => boolean;
};

const createActions = (
  $auth: AuthState,
  $searchPreferences: { proMode: boolean },
  $hotkeysStore: { open: boolean },
): HotkeyAction[] => [
  {
    id: "new-search",
    keys: "⌘ ⇧ O",
    description: "New Search",
    action: () => {
      if (get(page).url.pathname.startsWith(routes.searchesDev)) {
        goto(routes.searchesDev);
      } else {
        goto(routes.root);
      }
    },
    keyMatcher: (event: KeyboardEvent) => {
      return (event.metaKey || event.ctrlKey) && event.shiftKey && event.key.toLowerCase() === "o";
    },
  },
  {
    id: "edit-search",
    keys: "⌘ ⇧ ;",
    description: "Edit Latest Search",
    action: () => {
      searchFormOverlayStore.show();
    },
    keyMatcher: (event: KeyboardEvent) => {
      return (event.metaKey || event.ctrlKey) && event.shiftKey && event.key.toLowerCase() === ";";
    },
    isVisible: () => {
      return false;
      // return !!$auth.user;
    },
    isEnabled: () => {
      return false;
      // return !!$auth.user;
    },
  },
  {
    id: "toggle-dark-mode",
    keys: "⌘ ⇧ D",
    description: "Toggle Dark Mode",
    action: () => {
      if (get(mode) === "light") {
        setMode("dark");
      } else {
        setMode("light");
      }
    },
    keyMatcher: (event: KeyboardEvent) => {
      return (event.metaKey || event.ctrlKey) && event.shiftKey && event.key.toLowerCase() === "d";
    },
  },
  // {
  //   id: 'toggle-sidebar',
  //   keys: '⌘ B',
  //   description: 'Show / Hide Sidebar',
  //   action: () => {
  //     // document.dispatchEvent(new CustomEvent('toggleSidebar')); // default action
  //   },
  //   keyMatcher: (event: KeyboardEvent) => {
  //     return (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'b';
  //   },
  //   isVisible: () => {
  //     return false;
  //   },
  //   isEnabled: () => {
  //     return !!$auth.user;
  //   },
  // },
  {
    id: "toggle-pro",
    keys: "⌘ .",
    description: "Toggle Pro Mode",
    action: () => {
      if ($auth.user && ($auth.user.plan === SUBSCRIPTION_TIER.PRO || $auth.user.plan === SUBSCRIPTION_TIER.PLUS)) {
        searchPreferences.updatePreference("proMode", !$searchPreferences.proMode);
      } else {
        if ($searchPreferences.proMode) {
          searchPreferences.updatePreference("proMode", false);
        }
      }
    },
    keyMatcher: (event: KeyboardEvent) => {
      return (event.metaKey || event.ctrlKey) && event.key === ".";
    },
    isVisible: () => {
      return !!$auth.user;
    },
    isEnabled: () => {
      return !!(
        $auth.user &&
        ($auth.user.plan === SUBSCRIPTION_TIER.PRO || $auth.user.plan === SUBSCRIPTION_TIER.PLUS)
      );
    },
  },
  {
    id: "keyboard-shortcuts",
    keys: "⌘ /",
    description: "Show / Hide Keyboard Shortcuts",
    action: () => {
      showKeyboardShortcuts.set(!get(showKeyboardShortcuts));
    },
    keyMatcher: (event: KeyboardEvent) => {
      return (event.metaKey || event.ctrlKey) && event.key === "/";
    },
    isVisible: () => {
      return !!$auth.user;
    },
    isEnabled: () => {
      return !!$auth.user;
    },
  },
  {
    id: "command-menu",
    keys: "⌘ K",
    description: "Open / Close Command Menu",
    action: () => {
      hotkeysStore.setOpen(false);
    },
    isVisible: () => {
      return !!$auth.user && !$hotkeysStore.open;
    },
  },
  {
    id: "toggle-sidepanel",
    keys: "⌘ B",
    description: "Show / Hide Side Panel",
    action: () => {
      sidePanelStore.toggle();
    },
    keyMatcher: (event: KeyboardEvent) => {
      return (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "b";
    },
    isVisible: () => {
      return !!$auth.user;
    },
    isEnabled: () => {
      return !!$auth.user;
    },
  },
];

export const hotkeyActionsStore = derived(
  [authStore, searchPreferences, hotkeysStore, page],
  ([$auth, $searchPreferences, $hotkeysStore]) => {
    if (!browser) return [];
    return createActions($auth, $searchPreferences, $hotkeysStore);
  },
);

export const getHotkeyActions = () => {
  if (!browser) return [];
  return get(hotkeyActionsStore);
};
